import { FC, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { Progress, Row, Typography } from 'antd';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getPrimaryMarketItem } from 'features/primaryMarket/actions';

import { Loading, RejectedMessage, AgreeWithTerms } from 'componets/ui';
import { PrimaryMarketItemPhotos } from './PrimaryMarketItemPhotos';
import { PrimaryMarketProjectInvest } from './PrimaryMarketProjectInvest';
import { PrimaryItemPaymentSchedule } from './PrimaryMarketProjectPaymentSchedule';
import { PrimaryMarketProjectParams } from './PrimaryMarketProjectParams';
import { PrimaryMarketProjectNews } from './PrimaryMarketProjectNews';
import { ProjectTitle } from './ProjectTitle';

import { useAppSelector, useTranslate } from 'hooks';

import Utils from 'utils/utils';
import { RequestStatus } from 'utils/constants';
import { language } from 'utils/i18n';
import { AppDispatch } from 'store';
import { selectUser } from 'selectors';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';

import './index.scss';
import i18next from "i18next";

const WORDS = [
	'projectDescriptionText',
	'filledText',
	'projectAlreadyFundedText',
	'collateralInformationText',
	'documentsText',
	'baseRateText',
	'yourInvestmentProjectText',
];

export const PrimaryMarketProject: FC = () => {
	const {
		filledText,
		collateralInformationText,
		projectDescriptionText,
		projectAlreadyFundedText,
		documentsText,
		baseRateText,
		yourInvestmentProjectText,
	} = useTranslate(WORDS);

	const { item: project, status } = useAppSelector((state) => state.primaryMarket);
	const user = useAppSelector(selectUser);

	const dispatch = useDispatch<AppDispatch>();

	const { projectId } = useParams();

	useEffect(() => {
		if (projectId) {
			dispatch(getPrimaryMarketItem(projectId));
		}
	}, [dispatch, projectId]);

	const { collateral, projectDescription } = useMemo(() => {
		let collateral: string | null = null;
		let projectDescription: string | null = null;

		project.description?.forEach((item) => {
			if (item[language]?.key === 'collateral') {
				collateral = item[language]?.body;
			} else if (item[language]?.key === 'default') {
				projectDescription = item[language]?.body;
			}

			if (collateral && projectDescription) {
				return;
			}
		});

		return { collateral, projectDescription };
	}, [project.description]);

	const documents = useMemo(
		() => project.documents?.filter((item) => item.lang === language),
		[project.documents],
	);

	const isRejected = status === RequestStatus.REJECTED;

	const percent = ((+project.investedAmount / +project.assignedAmount) * 100).toFixed(2);

	return (
		<>
			{!user.agreeWithTerms && <AgreeWithTerms />}

			{isRejected ? (
				<RejectedMessage isNotFound />
			) : !project.country || (projectId && +projectId !== project?.loanId) ? (
				<Loading />
			) : (
				<div className="primaryItem">
					<div className="primaryItem__mobileTitle">
						<ProjectTitle />
					</div>

					<Row className="primaryItem__photos">
						{project.photos?.length > 0 && <PrimaryMarketItemPhotos images={project.photos} />}

						<div className="primaryItem__card">
							{project.possibleToInvest ? (
								<>
									<PrimaryMarketProjectInvest />
									<div className="primaryItem__cardInfo">
										<div className="primaryItem__progress">
											<Progress showInfo={false} percent={+percent} strokeColor="#65ABFF" />
											<div className="primaryItem__progressRow">
												<Typography.Text type="secondary">
													{percent}% {filledText}
												</Typography.Text>
												<Typography.Text type="secondary">
													<Trans
														i18nKey="leftText"
														values={{ money: Utils.moneyFormat(+project.availableToInvest) }}
													/>
												</Typography.Text>
											</div>
										</div>
									</div>
								</>
							) : (
								<div className="primaryItem__impossibleInvest">
									<Typography.Text>{projectAlreadyFundedText}</Typography.Text>

									<div className="primaryItem__impossibleProgress">
										<Progress showInfo={false} percent={100} strokeColor="#65ABFF" />
										<div className="primaryItem__progressRow">
											<Typography.Text type="secondary">100% {filledText}</Typography.Text>
										</div>
									</div>

									<div className="primaryItem__yourInvestment">
										<Typography.Text type="secondary">
											{yourInvestmentProjectText}:&nbsp;
										</Typography.Text>
										{Utils.moneyFormat(Number(project.amountOfInvestment))}
									</div>

									<Typography.Text className="primaryItem__link">
										<Trans
											i18nKey="viewAvailableProjectsText"
											components={{
												l: <Link to="/account/invest/primarymarket" />,
											}}
										/>
									</Typography.Text>
								</div>
							)}
						</div>
					</Row>

					<div className="primaryItem__info">
						<PrimaryMarketProjectParams />

						<div className="primaryItem__block">
							<Typography.Title className="primaryItem__blockTitle" level={4}>
								{baseRateText}
							</Typography.Title>
							<Typography.Text>{project.interest} %</Typography.Text>
						</div>

						{projectDescription && (
							<div className="primaryItem__block">
								<Typography.Title className="primaryItem__blockTitle" level={4}>
									{projectDescriptionText}
								</Typography.Title>
								<div
									className="primaryItem__projectList"
									dangerouslySetInnerHTML={{ __html: projectDescription }}
								/>
							</div>
						)}

						{collateral && (
							<div className="primaryItem__block">
								<Typography.Title className="primaryItem__blockTitle" level={4}>
									{collateralInformationText}
								</Typography.Title>
								<div
									className="primaryItem__projectList"
									dangerouslySetInnerHTML={{ __html: collateral }}
								/>
							</div>
						)}

						{documents?.length > 0 && (
							<div className="primaryItem__block">
								<Typography.Title className="primaryItem__blockTitle" level={4}>
									{documentsText}
								</Typography.Title>

								<div>
									{documents.map((item) => {
										return (
											<a
												key={item.path}
												href={item.path}
												target="_blank"
												rel="noreferrer"
												className="primaryItem__doc"
											>
												<DownloadIcon />
												<Typography.Text>{item.title}</Typography.Text>
											</a>
										);
									})}
								</div>
							</div>
						)}
					</div>

					<PrimaryItemPaymentSchedule
						data={project.paymentSchedule}
						archive={project.scheduleArchive}
						notes={project.params.filter((el) => el.pledgeKey === 'note' && el.language === i18next.language)}
					/>

					<PrimaryMarketProjectNews />
				</div>
			)}
		</>
	);
};
